import { Dialog } from '@mui/material';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import { CustomPasswordInput } from '@/components/molecules/CustomPasswordInput';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { PasswordRules } from '@/components/molecules/PasswordRules';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useTranslator } from '@/hooks/useTranslator';
import { PasswordValidationError, validatePassword } from '@/utility/passwordValidation';

interface ResetPasswordModalProps {
  readonly open: boolean;
  readonly loading?: boolean;

  onConfirm(currentPassword: string, newPassword: string): void;
  onClose(): void;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = (
  props: ResetPasswordModalProps,
): React.ReactElement => {
  const { open, onConfirm, onClose, loading = false } = props;
  const [currentPassword, setCurrentPassword] = React.useState<string>('');
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [validationError, setValidationError] = React.useState<PasswordValidationError>(
    PasswordValidationError.none,
  );

  const tr = useTranslator();

  const handleSubmit = React.useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();

      // Reset validation error
      setValidationError(PasswordValidationError.none);

      // Validate passwords match
      if (newPassword !== confirmPassword) {
        setValidationError(PasswordValidationError.passwordsDoNotMatch);
        return;
      }

      const requirementsError = validatePassword(newPassword);
      if (requirementsError !== PasswordValidationError.none) {
        setValidationError(requirementsError);
        return;
      }

      onConfirm(currentPassword, newPassword);
    },
    [currentPassword, newPassword, confirmPassword, onConfirm],
  );

  const handleClose = React.useCallback((): void => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setValidationError(PasswordValidationError.none);
    onClose();
  }, [onClose]);

  const handleCurrentPasswordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setCurrentPassword(event.target.value);
      setValidationError(PasswordValidationError.none);
    },
    [],
  );

  const handleNewPasswordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setNewPassword(event.target.value);
      setValidationError(PasswordValidationError.none);
    },
    [],
  );

  const handleConfirmPasswordChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setConfirmPassword(event.target.value);
      setValidationError(PasswordValidationError.none);
    },
    [],
  );

  return (
    <Dialog open={open} slotProps={slotProps}>
      <ApplicationModal
        title={tr('SET_NEW_PASSWORD')}
        width={552}
        variant="prominent"
        onClose={handleClose}>
        <Box component="form" sx={styles.form} onSubmit={handleSubmit}>
          <CustomPasswordInput
            label={tr('CURRENT_PASSWORD')}
            fullWidth={true}
            value={currentPassword}
            error={validationError !== PasswordValidationError.none}
            autoComplete="current-password"
            onChange={handleCurrentPasswordChange}
          />

          <CustomPasswordInput
            label={tr('NEW_PASSWORD')}
            fullWidth={true}
            value={newPassword}
            error={validationError !== PasswordValidationError.none}
            autoComplete="new-password"
            onChange={handleNewPasswordChange}
          />

          <CustomPasswordInput
            label={tr('CONFIRM_NEW_PASSWORD')}
            fullWidth={true}
            value={confirmPassword}
            error={validationError !== PasswordValidationError.none}
            autoComplete="new-password"
            onChange={handleConfirmPasswordChange}
          />

          {validationError !== PasswordValidationError.none && (
            <FormHelperText error={true}>{tr(validationError)}</FormHelperText>
          )}
          <PasswordRules />

          <Box sx={styles.buttonSection}>
            <LoadingButton
              type="submit"
              variant="contained"
              fullWidth={true}
              loading={loading}
              disabled={validationError !== PasswordValidationError.none}>
              {tr('SUBMIT')}
            </LoadingButton>
          </Box>
        </Box>
      </ApplicationModal>
    </Dialog>
  );
};

const styles: Record<string, SystemStyleObject> = {
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    width: '100%',
  },
  buttonSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 3,
    width: '100%',
  },
  cancelButton: {
    display: 'block',
    textAlign: 'center',
    cursor: 'pointer',
  },
};

const slotProps = {
  paper: {
    sx: {
      minWidth: 0,
      padding: 0,
    },
  },
};
